import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import PublicLayout from "../components/layout/PublicLayout";
import { api, formatMoney, formatApiError } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import PriceDisplay from "../components/PriceDisplay";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { carImageUrl } from "../components/cars/CarCard";
import { ShieldCheck, Smartphone, ArrowRight, CreditCard } from "lucide-react";
import NegotiateDialog from "../components/NegotiateDialog";

export default function GuestCheckout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currencies, selectedCurrency } = useCurrency();

  const carId = searchParams.get("car_id");
  const startDate = searchParams.get("start_date");
  const endDate = searchParams.get("end_date");
  const startTime = searchParams.get("start_time");
  const endTime = searchParams.get("end_time");
  const negotiate = searchParams.get("negotiate") === "1";
  const agreedPrice = searchParams.get("agreed_price") ? parseFloat(searchParams.get("agreed_price")) : null;
  const negId = searchParams.get("neg_id");
  const negToken = searchParams.get("token");
  const fromNegotiation = !!(agreedPrice && negId && negToken);

  const [car, setCar] = useState(null);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [payMethod, setPayMethod] = useState("mtn_momo");
  const [paymentCurrency, setPaymentCurrency] = useState("RWF"); // card payments only
  const [showNegotiate, setShowNegotiate] = useState(false);

  // Guest details — prefilled from negotiation if available
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [momoPhone, setMomoPhone] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    if (!carId || !startDate || !endDate) {
      toast.error("Missing booking details");
      navigate("/cars");
      return;
    }
    const basePromises = [
      api.get(`/cars/${carId}`),
      api.post("/bookings/quote", { car_id: carId, start_date: startDate, end_date: endDate }),
      fromNegotiation ? api.get(`/guest/negotiate/${negId}?token=${negToken}`) : Promise.resolve(null),
    ];
    Promise.all(basePromises).then(([carRes, quoteRes, negRes]) => {
      setCar(carRes.data);
      const q = quoteRes.data;
      // Override commission with agreed price if from negotiation
      if (agreedPrice) {
        const commissionRate = q.commission_rate || 0.15;
        const commission = Math.round(agreedPrice * commissionRate);
        const ownerNet = agreedPrice - commission;
        setQuote({ ...q, total: agreedPrice, commission, owner_net: ownerNet, subtotal: agreedPrice });
      } else {
        setQuote(q);
      }
      // Prefill guest details from negotiation
      if (negRes) {
        const neg = negRes.data;
        const nameParts = (neg.customer_name || "").split(" ");
        setFirstName(nameParts[0] || "");
        setLastName(nameParts.slice(1).join(" ") || "");
        setEmail(neg.customer_email || "");
        setPhone(neg.customer_phone || "");
        setMomoPhone(neg.customer_phone || "");
      }
    }).catch(e => {
      toast.error(formatApiError(e));
      navigate("/cars");
    }).finally(() => setLoading(false));
  }, [carId, startDate, endDate]);

  const channelMap = { mtn_momo: "MOMO", airtel: "AIRTEL_MONEY", card: "CARD" };

  const handleSubmit = async () => {
    if (!fromNegotiation && (!firstName || !lastName || !email || !phone)) {
      toast.error("Please fill in all your details");
      return;
    }
    if (!agreedToTerms) {
      toast.error("Please agree to the Terms & Conditions and Privacy Policy to continue.");
      return;
    }
    const channelName = channelMap[payMethod];
    let normalizedPhone = "";
    if (payMethod === "mtn_momo" || payMethod === "airtel") {
      if (!momoPhone) {
        toast.error(payMethod === "mtn_momo" ? "Please enter your MTN MoMo phone number" : "Please enter your Airtel Money phone number");
        return;
      }
      normalizedPhone = momoPhone.trim().replace(/\s/g, "");
      if (normalizedPhone.startsWith("0")) normalizedPhone = "+250" + normalizedPhone.slice(1);
      else if (normalizedPhone.startsWith("250")) normalizedPhone = "+" + normalizedPhone;
      else if (!normalizedPhone.startsWith("+")) normalizedPhone = "+250" + normalizedPhone;
    } else {
      // CARD — reuse the guest's general phone number, no MoMo-style normalization
      normalizedPhone = phone.trim();
    }
    setSubmitting(true);
    try {
      const { data: bookingData } = await api.post("/guest/booking", {
        car_id: carId,
        start_date: startDate,
        end_date: endDate,
        start_time: startTime || undefined,
        end_time: car?.fixed_return_time || endTime || undefined,
        first_name: firstName,
        last_name: lastName,
        email,
        phone,
        agreed_price: agreedPrice || undefined,
      });
      const { data: paymentData } = await api.post("/guest/payment/urubutopay", {
        booking_id: bookingData.booking_id,
        guest_token: bookingData.guest_token,
        channel_name: channelName,
        phone_number: normalizedPhone,
        payment_currency: channelName === "CARD" ? paymentCurrency : undefined,
      });
      if (channelName === "CARD" && paymentData.card_processing_url) {
        window.location.href = paymentData.card_processing_url;
        return;
      }
      toast.info(paymentData.message || "Payment request sent!");
      navigate(`/guest/verify?booking_id=${bookingData.booking_id}&token=${bookingData.guest_token}&session_id=${paymentData.session_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setSubmitting(false);
    }
  };

  if (loading) return <PublicLayout><div className="max-w-5xl mx-auto py-20 text-center text-muted-foreground">Loading…</div></PublicLayout>;

  return (
    <PublicLayout>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.2em] text-accent font-bold mb-2">Guest Checkout</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Complete your booking</h1>
          <p className="text-sm text-muted-foreground mt-1">No account needed. Pay booking fee and verify your identity to confirm.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-7 space-y-6">

            {/* How it works */}
            <Card className="border-border shadow-organic p-6">
              <div className="flex items-start gap-2.5">
                <ShieldCheck size={18} className="text-primary mt-0.5 shrink-0" />
                <div>
                  <div className="font-display font-semibold text-sm">How payment works</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Pay the <strong>{quote ? (quote.commission_rate * 100).toFixed(0) : 15}% non-refundable booking fee</strong> now to secure your dates.
                    After payment, upload your ID to confirm the booking and unlock the owner contact.
                    The remaining amount is paid directly to the owner at pickup.
                  </div>
                </div>
              </div>
            </Card>

            {/* Guest details — only show if NOT from negotiation */}
            {!fromNegotiation && (
              <Card className="border-border shadow-organic p-6 space-y-4">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Your details</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">First name</Label>
                    <Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Jean" className="mt-1.5 h-10" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Last name</Label>
                    <Input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Bosco" className="mt-1.5 h-10" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Email address</Label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5 h-10" />
                  <div className="text-[10px] text-muted-foreground mt-1">Booking confirmation will be sent here</div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Phone number</Label>
                  <Input value={phone} onChange={e => setPhone(e.target.value.replace(/[^0-9+\s]/g, ""))} placeholder="+250 788 123 456" className="mt-1.5 h-10" inputMode="tel" />
                </div>
              </Card>
            )}

            {/* Payment method */}
            <Card className="border-border shadow-organic p-6 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Payment method</div>

              {/* MTN MoMo */}
              <div onClick={() => setPayMethod("mtn_momo")}
                className={"p-4 rounded-xl border-2 cursor-pointer transition-colors " + (payMethod === "mtn_momo" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40")}>
                <div className="flex items-center gap-3 mb-2">
                  <Smartphone size={18} className="text-primary" />
                  <div className="font-semibold text-sm">MTN Mobile Money</div>
                  <span className="ml-auto text-[10px] bg-primary text-white px-2 py-0.5 rounded-full">Available</span>
                </div>
                {payMethod === "mtn_momo" && (
                  <div className="mt-2">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">MTN MoMo number</Label>
                    <Input value={momoPhone} onChange={e => setMomoPhone(e.target.value)} placeholder="+250 78x xxx xxx" className="mt-1.5 h-10" />
                    <div className="text-[10px] text-muted-foreground mt-1">You will receive a push notification to approve</div>
                  </div>
                )}
              </div>

              {/* Airtel Money */}
              <div onClick={() => setPayMethod("airtel")}
                className={"p-4 rounded-xl border-2 cursor-pointer transition-colors " + (payMethod === "airtel" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40")}>
                <div className="flex items-center gap-3 mb-2">
                  <Smartphone size={18} className="text-red-500" />
                  <div className="font-semibold text-sm">Airtel Money</div>
                  <span className="ml-auto text-[10px] bg-primary text-white px-2 py-0.5 rounded-full">Available</span>
                </div>
                {payMethod === "airtel" && (
                  <div className="mt-2">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Airtel Money number</Label>
                    <Input value={momoPhone} onChange={e => setMomoPhone(e.target.value)} placeholder="+250 73x xxx xxx" className="mt-1.5 h-10" />
                    <div className="text-[10px] text-muted-foreground mt-1">You will receive a prompt to approve</div>
                  </div>
                )}
              </div>

              {/* Bank Card */}
              <div onClick={() => setPayMethod("card")}
                className={"p-4 rounded-xl border-2 cursor-pointer transition-colors " + (payMethod === "card" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40")}>
                <div className="flex items-center gap-3">
                  <CreditCard size={18} className="text-blue-500" />
                  <div className="font-semibold text-sm">ATM / Bank Card</div>
                  <span className="ml-auto text-[10px] bg-primary text-white px-2 py-0.5 rounded-full">Available</span>
                </div>
                {payMethod === "card" && (
                  <div className="mt-2 space-y-2">
                    <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                      {["RWF", "USD"].map((c) => (
                        <button key={c} type="button" onClick={() => setPaymentCurrency(c)}
                          className={"px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors " + (paymentCurrency === c ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:bg-muted")}>
                          Pay in {c}
                        </button>
                      ))}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      You will be redirected to a secure page to enter your card details.
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Negotiate option */}
            {negotiate && car && (
              <div className="text-center">
                <button type="button" onClick={() => setShowNegotiate(true)}
                  className="text-[#E97A48] font-semibold text-sm hover:underline">
                  💰 Negotiate the Price
                </button>
              </div>
            )}

            {!fromNegotiation && (
              <div className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link to="/login" className="text-primary font-semibold hover:underline">Sign in</Link>
              </div>
            )}
          </div>

          {/* Order summary */}
          <aside className="md:col-span-5">
            <Card className="border-border shadow-organic p-6 md:sticky md:top-24">
              {car && (
                <div className="flex gap-3 mb-4">
                  <div className="w-20 h-20 rounded-md overflow-hidden bg-muted border border-border">
                    <img src={carImageUrl(car.images?.[0])} alt={car.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold truncate">{car.title}</div>
                    <div className="text-xs text-muted-foreground">{startDate} → {endDate}</div>
                    <div className="text-xs text-muted-foreground mt-1">{quote?.days} day{quote?.days > 1 ? "s" : ""}</div>
                    {agreedPrice && <div className="text-xs text-green-600 font-medium mt-1">✓ Negotiated price</div>}
                  </div>
                </div>
              )}
              <Separator className="my-4" />
              {quote && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Total rental cost</span>
                    <span><PriceDisplay amount={quote.total} listingCurrency={quote.currency} /></span>
                  </div>
                  <div className="flex justify-between font-display text-lg font-bold text-primary">
                    <span>Booking fee due now</span>
                    <span><PriceDisplay amount={quote.commission} listingCurrency={quote.currency} /></span>
                  </div>
                  <div className="flex justify-between text-muted-foreground text-xs">
                    <span>Pay to owner at pickup</span>
                    <span><PriceDisplay amount={quote.owner_net} listingCurrency={quote.currency} /></span>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1 p-2 bg-muted rounded-lg">
                    The {(quote.commission_rate * 100).toFixed(0)}% booking fee is non-refundable.
                  </div>
                </div>
              )}
              <div className="flex items-start gap-2 mt-4">
                <input
                  type="checkbox"
                  id="guest-agree-terms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-0.5"
                  data-testid="guest-agree-terms"
                />
                <label htmlFor="guest-agree-terms" className="text-xs text-muted-foreground">
                  I agree to RydeXpress&rsquo;s{" "}
                  <Link to="/legal/terms" target="_blank" className="text-primary hover:underline">Terms &amp; Conditions</Link>
                  {" "}and{" "}
                  <Link to="/legal/privacy" target="_blank" className="text-primary hover:underline">Privacy Policy</Link>.
                </label>
              </div>
              <Button onClick={handleSubmit} disabled={submitting} className="mt-3 w-full h-11 bg-accent hover:bg-accent/90 text-accent-foreground">
                {submitting ? "Processing…" : "Pay booking fee"}
                <ArrowRight size={16} className="ml-1.5" />
              </Button>
            </Card>
          </aside>
        </div>
      </div>
      {car && <NegotiateDialog open={showNegotiate} onOpenChange={setShowNegotiate} car={car} dates={{start: startDate, end: endDate}} isGuest={true} />}
    </PublicLayout>
  );
}
